export type WorkflowStage = string;

export const WORKFLOW_TYPE_DISPLAY_NAMES: Record<string, string> = {
  AnnualEvaluation: 'Annual Evaluation',
  FiveYearReview: 'Five Year Review',
  Merit: 'Merit',
  Promotion: 'Promotion',
  Goals: 'Goals',
  Deferral: 'Deferral',
  Indefinite: 'Indefinite',
};

export const STAGE_DISPLAY_NAMES: Record<string, string> = {
  CandidateSubmission: 'Candidate Submission',
  Supervisors: 'Supervisors',
  AdHocCommittee: 'Ad Hoc Committee',
  PeerReviewCommittee: 'Peer Review Committee',
  FinalReviewer: 'Final Reviewer',
  CandidateOverview: 'Candidate Overview',
  CandidateRebuttal: 'Candidate Rebuttal',
  LetterOfEvaluation: 'Letters of Evaluation',
  Complete: 'Complete'
};

/**
 * Workflow types whose stages open/close on calendar dates — so the candidate
 * has a real submission window, and may rescind while it is still open. Keep in
 * sync with WorkflowType.isDateGated on the server, which gates the rescind
 * endpoint on exactly this set.
 */
export const DATE_GATED_WORKFLOW_TYPES: readonly string[] =
  ['Promotion', 'Indefinite', 'Merit', 'FiveYearReview', 'AnnualEvaluation', 'Goals'];

/**
 * The subset of the above whose reviewer stages all run in parallel, so
 * workflow.Case.CurrentStage never leaves "CandidateSubmission" and is
 * meaningless — case state is derived from the date gates instead.
 * Annual Evaluation and Goals are date-gated but still advance their pointer
 * (Supervisors, then CandidateRebuttal), so their stage label is real.
 * Complement of WorkflowType.supportsSequentialAdvancement on the server.
 */
export const FROZEN_STAGE_WORKFLOW_TYPES: readonly string[] =
  ['Promotion', 'Indefinite', 'Merit', 'FiveYearReview'];

export enum WorkflowTaskStatus {
  Active = 'Active',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  Skipped = 'Skipped'
}

export interface WorkflowTaskSummary {
  id: number;
  caseId: number;
  candidateName: string;
  workflowType: string;
  periodOfReviewId: number;
  stage: string;
  status: WorkflowTaskStatus;
  dueDate?: string;
  createdAt: string;
  completedAt?: string;
  assignedToName?: string;
  assignedToEmail?: string;
  showTaskDetails: boolean;
  caseStatus: string;
  // Date gating (null/undefined when the stage is not date-gated). dueDate is
  // the effective date (period date + per-case override), recomputed server-side.
  gateStatus?: 'Upcoming' | 'Open' | 'PastDue' | null;
  gateOpenDate?: string | null;   // YYYY-MM-DD (effective: period date + per-case override)
  pastDue?: boolean;
}

/** A read-only reviewer role on the APR landing page (no workflow.Task — sourced
 *  from the reviewer dashboard's CRUD path via /workflow/landing-readonly-reviews). */
export interface LandingReviewItem {
  caseId: number;
  candidateName: string;
  stage: string;
  title: string;
  bucket: 'active' | 'upcoming' | 'pastDue';
  openDate: string | null;
  dueDate: string | null;
}

export interface WorkflowTaskDetail {
  id: number;
  caseId: number;
  candidateName: string;
  stage: string;
  status: WorkflowTaskStatus;
  assignedToEmail?: string;
  assignedToName?: string;
  createdAt: string;
  completedAt?: string;
  dueDate?: string;
  formData?: Record<string, any>;
  formSchema: FormFieldSchema[];
  attachments: WorkflowAttachment[];
  // Case summary
  workflowType: string;
  currentStage: string;
  caseStatus: string;
  periodOfReviewId: number;
  stages: string[];
  currentPositionLabel?: string;
  proposedPositionLabel?: string;
  periodOfReviewStartDate: string;
  periodOfReviewEndDate: string;
  result?: string;
  // Date gating (null/undefined when the stage is not date-gated)
  gateStatus?: 'Upcoming' | 'Open' | 'PastDue' | null;
  gateOpenDate?: string | null;   // YYYY-MM-DD (effective: period date + per-case override)
  gateDueDate?: string | null;    // YYYY-MM-DD
  editable?: boolean;
}

export interface WorkflowPacketDetail {
  caseId: number;
  academicId: string;
  candidateName: string;
  workflowType: string;
  currentStage: string;
  status: string;
  createdAt: string;
  currentPositionLabel?: string;
  proposedPositionLabel?: string;
  periodOfReviewId: number;
  periodOfReviewStartDate: string;
  periodOfReviewEndDate: string;
  result?: string;
  hasAdminAssignment: boolean;
  supportsAdminAssignment: boolean;
  stages: string[];
  tasks: WorkflowTaskSummary[];
  attachments: WorkflowAttachment[];
}

export interface FormFieldSchema {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'date' | 'file' | 'documentList' | 'committeeMembers';
  required: boolean;
  options?: { value: string; label: string }[];
  validation?: { minLength?: number; maxLength?: number; pattern?: string };
}

export interface WorkflowAttachment {
  id: number;
  fileId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  downloadUrl: string;
  documentSlot: string;
  stageName: string | null;
  uploadedAt: string;
}

export interface DocumentSlotData {
  name: string;
  note?: string;
  minCount: number;
  maxCount: number;
  attachmentCount: number;
  pageLimit?: number;
}

export interface CommitteeMemberData {
  name: string;
  email?: string;
  displayName?: string;
}

export interface WorkflowCaseIndex {
  caseId: number;
  candidateName: string;
  workflowType: string;
  currentStage: string;
  status: string;
  /** Summary case state ("With candidate" / "In review" / "Complete" …),
   *  derived server-side from gate dates — the index column to display. */
  statusSummary: string;
  periodOfReviewId: number;
  createdAt: string;
}

export interface ActiveWorkflowCase {
  caseId: number;
  workflowType: string;
  currentStage: string;
  status: string;
  periodOfReviewId: number;
  stallReason: string | null;
  hasAdminAssignment: boolean;
  /** True only for workflow types that have a page-limited document slot (Merit/Promotion/Indefinite). Gates the admin-assignment checkbox. */
  supportsAdminAssignment: boolean;
}

export interface WorkflowTimelineEvent {
  stage: string;
  status: WorkflowTaskStatus;
  assignedToName?: string;
  completedAt?: string;
  createdAt: string;
}
